import PropTypes from "prop-types";
import { useState } from "react";
import AddressSearchStatus from "./AddressSearchStatus";
import {
  Combobox,
  ComboboxInput,
  ComboboxPopover,
  ComboboxList,
  ComboboxOption,
} from "@reach/combobox";
import "@reach/combobox/styles.css";
import useMarketPlacesAutocomplete from "../../hooks/useMarketPlacesAutocomplete";
import { geocodeAddress, latLngFromGeocode } from "../../utils/googlePlaces";
import { normalizeMarketLatLng } from "../../config/market";
import reportClientError from "../../utils/clientTelemetry";

// ── Reusable Google Places address field ────────────────────────────────────
// Resolves a typed address to {lat,lng} the exact same way the shipping screens
// do (country=ug, establishment bias). Shared by SavedAddressPicker (checkout)
// and the SavedAddresses profile manager so address capture lives in one place.
//
// onChange(text)        — fires as the user types / picks a suggestion.
// onResolved({ latLng, locationId, formatted }) — fires once coordinates are
//   resolved for a chosen suggestion (the field every consumer needs).

const ACCENT = "#03660c";

const comboInputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "9px 10px",
  marginBottom: "4px",
  border: "1px solid #d6dcde",
  borderRadius: "8px",
  fontSize: "16px",
  minHeight: "48px",
  outline: "none",
};

const ErrorText = ({ children }) => (
  <div role="alert" style={{ color: "#a43118", fontSize: "12px", margin: "4px 0 6px" }}>
    {children}
  </div>
);

ErrorText.propTypes = {
  children: PropTypes.node.isRequired,
};

const AddressAutocomplete = ({ value, onChange, onResolved, onError, placeholder }) => {
  const [resolving, setResolving] = useState(false);
  const {
    value: term,
    suggestions: { data, loading, status },
    setValue,
    clearSuggestions,
    ready,
    error,
  } = useMarketPlacesAutocomplete({
    defaultValue: value,
  });

  const handleSelect = async (selected) => {
    setValue(selected, false);
    onChange(selected);
    clearSuggestions();
    setResolving(true);
    try {
      const results = await geocodeAddress(selected);
      const raw = latLngFromGeocode(results[0]);
      const latLng = normalizeMarketLatLng(raw.lat, raw.lng);
      if (!latLng) throw new Error("The selected address is outside this delivery market");
      onResolved({
        latLng,
        locationId: results[0].place_id,
        formatted: selected,
      });
    } catch (err) {
      reportClientError(err, { area: "saved_address_geocode" });
      if (onError) onError(err);
    } finally {
      setResolving(false);
    }
  };

  return (
    <>
      <Combobox onSelect={handleSelect} aria-label="Delivery address">
        <ComboboxInput
          value={term}
          onChange={(e) => {
            setValue(e.target.value);
            onChange(e.target.value);
          }}
          disabled={!ready || resolving}
          aria-busy={loading || resolving}
          placeholder={ready ? placeholder || "Search delivery address..." : "Loading address search…"}
          style={comboInputStyle}
        />
        <ComboboxPopover
          style={{
            background: "#fff",
            border: `1px solid ${ACCENT}`,
            borderRadius: "8px",
            fontSize: "14px",
            maxWidth: "calc(100vw - 24px)",
            boxSizing: "border-box",
            boxShadow: "0 2px 10px rgba(0,0,0,0.18)",
            zIndex: 5,
          }}
        >
          <ComboboxList style={{ maxHeight: "min(240px, 35dvh)", overflowY: "auto", overflowX: "hidden", margin: 0, padding: 0 }}>
            {data.map((s) => (
              <ComboboxOption key={s.place_id} value={s.description} style={{ whiteSpace: "normal", overflowWrap: "anywhere", minHeight: 44, boxSizing: "border-box", padding: "12px", lineHeight: 1.4 }} />
            ))}
          </ComboboxList>
        </ComboboxPopover>
      </Combobox>
      <AddressSearchStatus loading={loading} ready={ready} status={status} count={data.length} error={error} resolving={resolving} />
      {error && (
        <ErrorText>
          Address search is temporarily unavailable. Retry the map service or use a valid saved address.
        </ErrorText>
      )}
    </>
  );
};

AddressAutocomplete.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onResolved: PropTypes.func.isRequired,
  onError: PropTypes.func,
  placeholder: PropTypes.string,
};

export default AddressAutocomplete;
